import { API_PREFIX, type ApiErrorBody } from "@taskiq/shared";
import { currentAccessToken, supabase } from "./supabase";

const BASE = import.meta.env.VITE_API_URL ?? "";

/**
 * Every failure the app can see, in one shape.
 *
 * **Branch on `code`, never on `message`.** The code is a contract the API
 * commits to; the message is prose written for a human and gets reworded.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: unknown;
    requestId?: string;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** No response at all: offline, DNS failure, or the API is down. */
  get isNetwork(): boolean {
    return this.status === 0;
  }

  /**
   * A valid session with no workspace behind it: a self sign-up that has not
   * finished. **The most ordinary state in the product, and never red** - the
   * console routes it to `/onboarding`.
   *
   * Kept separate from `isPending` because the two need opposite screens. This
   * one is a form the user completes in fifteen seconds; the other is a dead end
   * they have to ask someone else to fix.
   */
  get needsOnboarding(): boolean {
    return this.code === "onboarding_required";
  }

  /**
   * The account exists but cannot be used, and the caller cannot fix it from
   * here. Two codes, because the remedy differs: `account_disabled` is something
   * an administrator at their own company can reverse, `company_disabled` is not
   * - the whole tenant is suspended and everyone who could help is locked out
   * too. The screen says so; see `RequireAuth`.
   */
  get isPending(): boolean {
    return this.code === "account_disabled" || this.code === "company_disabled";
  }

  /** True only for the tenant-wide suspension, which needs different copy. */
  get isCompanyDisabled(): boolean {
    return this.code === "company_disabled";
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Set false for a route that must not carry a token. Rare. */
  auth?: boolean;
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const { body, auth = true, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (body !== undefined) finalHeaders.set("Content-Type", "application/json");

  if (auth) {
    const token = await currentAccessToken();
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  }

  try {
    return await fetch(`${BASE}${API_PREFIX}${path}`, {
      ...rest,
      headers: finalHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // A rejected fetch means the request never reached the server. Normalise it
    // to status 0 so callers have one thing to check rather than a mix of
    // TypeError and ApiError.
    throw new ApiError({
      status: 0,
      code: "network",
      message: "Could not reach the server. Check your connection and try again.",
    });
  }
}

/**
 * One shared refresh, however many requests 401 around the same moment.
 *
 * A page mounts several `useApi` calls at once, and if the token they were all
 * about to use has gone stale, they all discover it within milliseconds of each
 * other. Without sharing this, each one would call `refreshSession()`
 * independently - Supabase Cloud rotates refresh tokens, so the second call
 * would be spending a token the first one already burned, and would fail even
 * though the first one succeeded.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = supabase.auth
      .refreshSession()
      .then(({ data, error }) => !error && Boolean(data.session))
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await request(path, options);

  /**
   * A 401 does not, on its own, mean the session is dead - it means the token
   * *this request* carried was rejected, and that can be stale news rather than
   * the truth: `autoRefreshToken`'s timer, another tab rotating the same
   * refresh token, or simply losing a race between "grab the token" and "the
   * response comes back" can all produce one. The fix for a stale token is to
   * get a current one and try again, not to end the session - the previous
   * version of this function only decided whether to *punish* a 401 like this,
   * which stopped the false sign-out but still left the page looking broken,
   * because the request that actually mattered was never retried.
   *
   * `refreshOnce()` is shared, so a page that 401s on five parallel requests
   * spends exactly one refresh, and every one of them retries against
   * whatever it produced.
   */
  if (response.status === 401 && (await refreshOnce())) {
    response = await request(path, options);
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let body: ApiErrorBody | undefined;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      /* HTML error page from a proxy, or an empty body */
    }

    const error = new ApiError({
      status: response.status,
      code: body?.error?.code ?? "internal_error",
      message: body?.error?.message ?? fallbackMessage(response.status),
      details: body?.error?.details,
      requestId: body?.error?.requestId ?? response.headers.get("x-request-id") ?? undefined,
    });

    // Reaching here on a 401 means a fresh token could not save it: either the
    // refresh above failed outright (no session at all - nothing to sign out
    // of, but the call is harmless) or the retry it earned was *also* refused.
    // Either way the account genuinely has no usable session, which is the one
    // case this function is right to end unilaterally: `useAuth().status` can
    // only ever learn the session died through this listener firing, so
    // skipping it would leave a page that thinks it is signed in retrying into
    // the same wall forever instead of being sent back to `/login`.
    //
    // A 403 is deliberately NOT handled here: a pending account, or a session
    // that is simply not allowed to read this one resource, is a legitimate
    // signed-in state, and signing them out would make it impossible to show
    // the "awaiting approval" screen - or would sign someone out of an entire
    // console session over a single screen they cannot open.
    if (response.status === 401) {
      await supabase.auth.signOut();
    }

    throw error;
  }

  return (await response.json()) as T;
}

function fallbackMessage(status: number): string {
  if (status >= 500) return "Something went wrong on our end. Try again in a moment.";
  if (status === 404) return "That could not be found.";
  if (status === 429) return "Too many requests. Wait a moment and try again.";
  return "The request could not be completed.";
}

/** Safe message extraction for a `catch (err: unknown)`. */
export function apiErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * The per-field messages of a `validation_error`, first message per field.
 *
 * The API sends zod's `flatten()` as `details`, which keys by the top-level
 * field of whatever it parsed. Empty for every other failure, so a caller can
 * spread it into its error map unconditionally.
 */
export function apiFieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || err.code !== "validation_error") return {};
  const fields = (err.details as { fieldErrors?: Record<string, string[] | undefined> } | undefined)
    ?.fieldErrors;
  const out: Record<string, string> = {};
  for (const [key, messages] of Object.entries(fields ?? {})) {
    if (messages?.[0]) out[key] = messages[0];
  }
  return out;
}
